// ============================================================================
// JARVISTRAVEL - BUDGET ENGINE (client-side, model-ready)
// Front-end stand-in for the Budget Engine forecast service (Hermes / JAR-139).
// Turns a trip's budget + itinerary into PREDICTIVE intelligence: per-category
// projections, the 7 trend states, projected exhaustion windows, and plain-
// language drivers — never "80% used". Emits the spec §7 DTO shapes so a real
// /budget/summary + /budget/forecast endpoint drops in behind it.
//
// NOTE: works in major currency units (the FE's existing representation). The
// real DTOs use integer minor units; the swap converts at the boundary.
// ============================================================================

import type { JournalTrip, Activity, ItineraryDay, BudgetCategory } from '@/app/types';
import type { BudgetStatus } from './budget';
import { confidenceLabel, stopIsPriced } from './budget';

// Which budget category an itinerary item rolls up to.
/**
 * Which budget category an itinerary item's cost lands in.
 *
 * Exported because it is a shared invariant, not a local convenience: the
 * forecast counts a category's committed spend with this map, and
 * BudgetManager's rebalance floor has to refuse to cut BELOW that same number.
 * They were two copies and they drifted — BudgetManager's omitted `wellness`,
 * so a booked spa item counted as committed in the forecast while the floor
 * treated it as free money the rebalance could take away. One map, or the two
 * disagree again the next time an item type is added.
 */
export const CATEGORY_OF: Record<string, string> = {
  flight: 'flights',
  accommodation: 'hotels',
  restaurant: 'food',
  attraction: 'activities',
  tour: 'activities',
  entertainment: 'activities',
  wellness: 'activities',
  transport: 'transport',
  shopping: 'shopping',
};

// JAR-2624: Flights and Hotels belong in a trip's budget only when the trip has
// one. These two are gated; every other category is always shown. "flights" is
// backed by a flight stop on the itinerary (`hasFlight`); "hotels" by an
// accommodation record, a hotel expense, or an approved lodging charge
// (`hasStay`). Money already spent in either stands in for the expense/charge
// cases, so a category that has taken real spend is never hidden.
const GATE_META: Record<string, { gate: 'hasFlight' | 'hasStay'; name: string; color: string }> = {
  flights: { gate: 'hasFlight', name: 'Flights', color: '#0EA5E9' },
  hotels: { gate: 'hasStay', name: 'Hotels', color: '#6E4A62' },
};

const round2 = (n: number): number => Math.round(n * 100) / 100;

/** Whether the trip has a real flight on it — a flight stop on the itinerary.
 *  The planner no longer seeds one (JAR-2607), so this is only a flight the
 *  person added (widget) or gave to Jarvis. */
export function tripHasFlight(trip: JournalTrip): boolean {
  return trip.itinerary.some((d) => (d.activities as Activity[]).some((a) => a.type === 'flight'));
}

/**
 * The budget categories to SHOW for a trip (JAR-2624). A gated category
 * (Flights, Hotels) with no backing is dropped, and its allocation is spread
 * across the rest in proportion to what they hold, so the total is unchanged.
 * A backed gated category the stored set is missing — a flight or stay added
 * after the budget was built — is shown at zero for the person or an
 * expense/charge to fund. Pure, and the single source the budget screen
 * renders, saves and audits through, so a hidden category can't resurface or
 * be counted twice.
 */
export function shownBudgetCategories(
  categories: BudgetCategory[],
  signals: { hasFlight: boolean; hasStay: boolean },
): BudgetCategory[] {
  const backed = (c: BudgetCategory): boolean => {
    const meta = GATE_META[c.id];
    return !meta || signals[meta.gate] || c.spent > 0;
  };
  const kept = categories.filter(backed);
  const freed = categories.reduce((sum, c) => (backed(c) ? sum : sum + c.allocated), 0);

  for (const [id, meta] of Object.entries(GATE_META)) {
    if (signals[meta.gate] && !kept.some((c) => c.id === id)) {
      kept.push({ id, name: meta.name, icon: id, color: meta.color, allocated: 0, spent: 0 });
    }
  }

  if (freed <= 0 || kept.length === 0) return kept;
  const base = kept.reduce((sum, c) => sum + c.allocated, 0);
  const grown = base > 0
    ? kept.map((c) => ({ ...c, allocated: round2(c.allocated + (freed * c.allocated) / base) }))
    : kept.map((c) => ({ ...c, allocated: round2(c.allocated + freed / kept.length) }));
  // Put any rounding remainder on the largest, so the parts sum to the original
  // total exactly (Fury: the total must not move).
  const target = grown.reduce((bi, c, i, arr) => (c.allocated > arr[bi].allocated ? i : bi), 0);
  const originalTotal = categories.reduce((sum, c) => sum + c.allocated, 0);
  const shownTotal = grown.reduce((sum, c) => sum + c.allocated, 0);
  grown[target] = { ...grown[target], allocated: round2(grown[target].allocated + (originalTotal - shownTotal)) };
  return grown;
}

/** Whether a category is one of the two gated by a trip signal (Flights, Hotels).
 *  Every other category is always shown. */
export function isGatedCategory(id: string): boolean {
  return id in GATE_META;
}

/** Add a gated category (Flights/Hotels) at zero if the set lacks it — used when
 *  a charge or a hand-entered expense first files against one that was never
 *  seeded (JAR-2624 triggers 3 and 4). A non-gated id, or one already present,
 *  returns the set unchanged. */
export function ensureGatedCategory(categories: BudgetCategory[], id: string): BudgetCategory[] {
  const meta = GATE_META[id];
  if (!meta || categories.some((c) => c.id === id)) return categories;
  return [...categories, { id, name: meta.name, icon: id, color: meta.color, allocated: 0, spent: 0 }];
}

/** The categories to OFFER when filing an expense: the set plus any gated
 *  category it lacks, so a flight or hotel expense can always be entered — which
 *  is itself the trigger that brings the category in (JAR-2624 trigger 3). */
export function withGatedOffers(categories: BudgetCategory[]): BudgetCategory[] {
  return Object.keys(GATE_META).reduce((cats, id) => ensureGatedCategory(cats, id), categories);
}

// Categories whose spend wobbles day-to-day (forecastable + at-risk); the rest
// (flights, hotels) are mostly known/locked once booked.
const VARIABLE = new Set(['food', 'activities', 'transport', 'shopping']);
const VARIABLE_BUFFER = 0.18; // unplanned wobble on variable categories

const CATEGORY_NOUN: Record<string, string> = {
  food: 'meals', activities: 'activities', transport: 'rides', shopping: 'purchases',
  flights: 'flights', hotels: 'nights',
};

export interface CategoryForecast {
  category_code: string;
  display_name: string;
  budget: number;
  actual_to_date: number;
  scheduled_future: number;
  forecast_variable: number;
  projected_total: number;
  projected_delta: number; // projected − budget (positive = over)
  /** Day index (1-based) into the trip the category is projected to run out. */
  projected_exhaustion_day?: number;
  projected_exhaustion_window?: string;
  trend_status: BudgetStatus;
  primary_drivers: string[];
}

export interface BudgetSummary {
  status: BudgetStatus;
  /** How many priced stops no total could include, because their price is in
   *  another currency (JAR-2036). A COUNT of stops, not an amount: the amounts are
   *  in different currencies, so summing them would be the very thing this
   *  prevents. Zero everywhere today; the forecast card says so when it is not. */
  uncounted_items: number;
  total_budget: number;
  actual_to_date: number;
  projected_total: number;
  projected_delta: number;
  confidence: number;
  confidence_label: 'low' | 'medium' | 'high';
  primary_driver: string;
  next_best_action: string;
}

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));
const round = (n: number) => Math.round(n);

const tripDayCount = (trip: JournalTrip): number => Math.max(1, trip.itinerary.length || 1);

/**
 * Whether a stop's cost can be ADDED to a total kept in `currency`.
 *
 * A stop carries `costCurrency` when its price is in a currency of its own: an
 * Explore experience quoted by its provider (every provider core has quotes USD),
 * or a planner-priced stop abroad, which core#1057 now answers back with on a cold
 * load (JAR-2409). Adding a euro price into a dollar total is a wrong number with
 * nothing on screen to say so, and there is no rate here to convert with, so a cost
 * that cannot be added is left out and counted instead (costsInOtherCurrencies
 * drives the forecast's notice). Brent's call, 2026-09-25: carry the currency, and
 * refuse to mix.
 */
export function costCountsIn(a: Pick<Activity, 'cost' | 'costCurrency'>, currency: string): boolean {
  if (!stopIsPriced(a.cost)) return false;
  // Every saved stop now carries a currency: toItineraryItem stamps the stop's own
  // or the trip's, and core#1057 answers with it. So an absent currency is an
  // unsaved page-only stop or a pre-#1057 legacy row, and counts in the budget's
  // currency rather than being hidden from every total (JAR-2409 box 3).
  if (!a.costCurrency) return true;
  return a.costCurrency.toUpperCase() === (currency || 'USD').toUpperCase();
}

/** A stop's cost as a total in `currency` may count it: the amount, or zero for one
 *  priced in something else. The exclude-or-zero rule, in one place, because it was
 *  written twice and a rule written twice drifts (#546 review). */
export function costInCurrency(a: Pick<Activity, 'cost' | 'costCurrency'>, currency: string): number {
  return costCountsIn(a, currency) ? (a.cost ?? 0) : 0;
}

/** How many of a trip's priced stops no total can include, because their price is
 *  in another currency. Non-zero once a trip holds a planner-priced stop abroad,
 *  whose currency core#1057 now keeps across a cold load (JAR-2409); it drives the
 *  forecast's uncounted notice (BudgetForecast.tsx). */
export function costsInOtherCurrencies(trip: JournalTrip): number {
  let n = 0;
  for (const day of trip.itinerary) {
    for (const a of day.activities as Activity[]) {
      if (a.cost !== undefined && a.cost !== null && !costCountsIn(a, trip.budget.currency)) n += 1;
    }
  }
  return n;
}

/** Sum the planned cost of itinerary items that roll up to a category. */
function plannedFor(trip: JournalTrip, code: string): { total: number; count: number } {
  let total = 0;
  let count = 0;
  for (const day of trip.itinerary) {
    for (const a of day.activities as Activity[]) {
      if (CATEGORY_OF[a.type] === code && costCountsIn(a, trip.budget.currency)) { total += a.cost ?? 0; count += 1; }
    }
  }
  return { total, count };
}

/** Forecast one budget category from its allocation, spend, and planned items. */
export function forecastCategory(trip: JournalTrip, category: { id: string; name: string; allocated: number; spent: number }): CategoryForecast {
  const code = category.id;
  const budget = category.allocated;
  const actual = category.spent;
  const variable = VARIABLE.has(code);
  const { total: planned, count } = plannedFor(trip, code);

  const scheduled_future = Math.max(0, planned - actual);
  let forecast_variable = variable ? round((actual + scheduled_future) * VARIABLE_BUFFER) : 0;

  // No itinerary pricing signal but a real allocation → we can't project well. The
  // signal is whether any stop is priced (count), not whether the planned total is
  // non-zero: a category of deliberately-$0 stops is priced (count > 0) yet sums to 0,
  // and must not read as "no signal" and contradict the confidence label (JAR-2533).
  const lowSignal = count === 0 && budget > 0 && actual < budget * 0.5;
  if (lowSignal) forecast_variable = variable ? round(budget * 0.1) : 0;

  const projected_total = lowSignal
    ? Math.max(actual, variable ? round(budget * 1.05) : budget)
    : actual + scheduled_future + forecast_variable;
  const projected_delta = projected_total - budget;

  // Trend state.
  let trend_status: BudgetStatus;
  if (budget > 0 && actual >= budget) trend_status = 'budget_exhausted';
  else if (lowSignal) trend_status = 'low_confidence';
  else if (projected_delta > budget * 0.05) trend_status = variable ? 'exhaustion_risk' : 'trending_over';
  else if (projected_delta > -budget * 0.05) trend_status = 'elevated_pace';
  else trend_status = 'healthy_pace';

  // Projected exhaustion point (only when actually over).
  let projected_exhaustion_day: number | undefined;
  let projected_exhaustion_window: string | undefined;
  if ((trend_status === 'exhaustion_risk' || trend_status === 'budget_exhausted') && projected_total > actual) {
    const frac = clamp((budget - actual) / (projected_total - actual), 0, 1);
    projected_exhaustion_day = clamp(round(frac * tripDayCount(trip)), 1, tripDayCount(trip));
    projected_exhaustion_window = code === 'food' ? 'dinner' : undefined;
  }

  // Plain-language drivers.
  const drivers: string[] = [];
  const noun = CATEGORY_NOUN[code] ?? 'items';
  if (projected_delta > 0 && scheduled_future > 0 && count > 0) drivers.push(`${count} planned ${noun} still ahead`);
  if (variable && forecast_variable > budget * 0.08) drivers.push('day-to-day spending tends to add up');
  if (actual > budget * 0.8 && trend_status !== 'budget_exhausted') drivers.push('most of this budget is already committed');
  if (lowSignal) drivers.push(`${noun} aren’t priced yet`);
  if (drivers.length === 0) drivers.push(trend_status === 'healthy_pace' ? 'on track with the plan' : 'close to plan');

  return {
    category_code: code,
    display_name: category.name,
    budget,
    actual_to_date: actual,
    scheduled_future,
    forecast_variable,
    projected_total,
    projected_delta,
    projected_exhaustion_day,
    projected_exhaustion_window,
    trend_status,
    primary_drivers: drivers.slice(0, 2),
  };
}

export interface MethodSplit {
  card: { budget: number; spent: number; remaining: number };
  cash: { budget: number; spent: number; remaining: number };
  /** Whether a cash portion is actually configured (else it's all card). */
  hasSplit: boolean;
}

/** Split the budget + spend into card vs cash. Budget split comes from
 *  cardBudget/cashBudget; spend from each transaction's method (default card),
 *  with any unattributed spend rolled into card. */
export function paymentSplit(trip: JournalTrip): MethodSplit {
  const b = trip.budget;
  const cashBudget = b.cashBudget ?? 0;
  const cardBudget = b.cardBudget ?? Math.max(0, b.total - cashBudget);
  const hasSplit = cashBudget > 0;

  let cashSpent = 0;
  let cardSpent = 0;
  for (const tx of b.transactions ?? []) {
    if (tx.method === 'cash') cashSpent += tx.amount;
    else cardSpent += tx.amount;
  }
  // Reconcile: attribute spend not covered by tagged transactions to card.
  const untagged = b.spent - (cashSpent + cardSpent);
  if (untagged > 0) cardSpent += untagged;

  return {
    card: { budget: cardBudget, spent: cardSpent, remaining: Math.max(0, cardBudget - cardSpent) },
    cash: { budget: cashBudget, spent: cashSpent, remaining: Math.max(0, cashBudget - cashSpent) },
    hasSplit,
  };
}

export interface DaySpend {
  date: string;
  dayNumber: number;
  estimated: number; // total planned cost for the day
  committed: number; // booked/locked portion (proxy for actual until receipts/Plaid)
}

/** Per-day planned vs committed spend — the dashboard's daily timeline. */
export function budgetByDay(trip: JournalTrip): DaySpend[] {
  return trip.itinerary.map((day) => {
    let estimated = 0;
    let committed = 0;
    for (const a of day.activities as Activity[]) {
      const c = costInCurrency(a, trip.budget.currency);
      estimated += c;
      if (a.booked) committed += c;
    }
    return { date: day.date, dayNumber: day.dayNumber, estimated, committed };
  });
}

/** Planned spend for a single day (sum of its activity costs), in `currency`.
 *  A stop priced in anything else is left out rather than added blind. */
export const dayActivityCost = (day: ItineraryDay, currency: string): number =>
  (day.activities as Activity[]).reduce((sum, a) => sum + costInCurrency(a, currency), 0);

export interface DayBudgetComparison {
  baselineCost: number;
  simulatedCost: number;
  /** simulated − baseline (negative = this day costs less after the change). */
  costDelta: number;
  /** Cost that left this day (positive when activities moved out). */
  movedCost: number;
  /** The activity whose cost moved, and where it went (from the rebalance moves). */
  movedActivity?: string;
  movedTo?: string;
}

/**
 * The budget read on a "make this day lighter" scenario. Lightening *redistributes*
 * spend (an activity moves to another day) rather than cutting it, so the day's
 * cost drops while the trip total is unchanged — this surfaces that honestly so
 * the Budget Engine and the Fatigue Index tell one consistent story.
 */
export function compareDayBudget(
  baseDay: ItineraryDay,
  simDay: ItineraryDay,
  moves: { activity: string; fromDate: string; toDate: string }[] = [],
  /** The currency both sides are read in. A stop priced in another is left out of
   *  both, because a delta that mixes currencies says nothing (JAR-2036). */
  currency = 'USD',
): DayBudgetComparison {
  const baselineCost = dayActivityCost(baseDay, currency);
  const simulatedCost = dayActivityCost(simDay, currency);
  return {
    baselineCost,
    simulatedCost,
    costDelta: simulatedCost - baselineCost,
    movedCost: Math.max(0, baselineCost - simulatedCost),
    movedActivity: moves[0]?.activity,
    movedTo: moves[0]?.toDate,
  };
}

/**
 * Forecasts for the budget categories a trip SHOWS, heaviest projected overage
 * first. Flights and Hotels are dropped when the trip has no flight / no stay
 * (JAR-2624), so the forecast, the trip rollup and every alert built on them
 * reads the same shown set a user sees — no hidden category trends over.
 * `hasFlight` is read from the trip; `hasStay` must be passed by a caller that
 * knows the trip's accommodations (BudgetManager), and otherwise a hotel is
 * shown only once it has taken spend.
 */
export function categoryForecasts(trip: JournalTrip, signals?: { hasStay?: boolean }): CategoryForecast[] {
  return shownBudgetCategories(trip.budget.categories ?? [], {
    hasFlight: tripHasFlight(trip),
    hasStay: signals?.hasStay ?? false,
  })
    .map((c) => forecastCategory(trip, c))
    .sort((a, b) => b.projected_delta - a.projected_delta);
}

/** What share of planned itinerary spend is actually priced — drives confidence. */
function pricedFraction(trip: JournalTrip): number {
  let priced = 0;
  let total = 0;
  for (const day of trip.itinerary) {
    for (const a of day.activities as Activity[]) {
      // Priced means priced in a currency this projection can add. A stop the
      // totals exclude is not knowledge about the plan's cost, and counting it
      // held confidence high over an understated projection (#546 review).
      if (CATEGORY_OF[a.type]) { total += 1; if (costCountsIn(a, trip.budget.currency)) priced += 1; }
    }
  }
  return total === 0 ? 0.4 : priced / total;
}

/** Trip-level rollup — the Budget Summary DTO (spec §7). Reads the same shown
 *  category set as `categoryForecasts`, so a hidden Flights/Hotels never drives
 *  the headline or the projected total (JAR-2624). */
export function summarizeBudget(trip: JournalTrip, signals?: { hasStay?: boolean }): BudgetSummary {
  const cats = categoryForecasts(trip, signals);
  const uncounted_items = costsInOtherCurrencies(trip);
  const total_budget = trip.budget.total;
  const actual_to_date = trip.budget.spent;
  const projected_total = cats.length
    ? cats.reduce((s, c) => s + c.projected_total, 0)
    : actual_to_date;
  const projected_delta = projected_total - total_budget;

  // Confidence from how much of the plan is priced, tightened by trip size.
  const confidence = clamp(0.4 + pricedFraction(trip) * 0.5, 0.3, 0.95);

  // Trip status reflects the TRIP projection (a single small category over its
  // cap shouldn't flag the whole trip). Per-category risk lives on the cards +
  // the driver line below.
  let status: BudgetStatus;
  if (actual_to_date >= total_budget) status = 'budget_exhausted';
  else if (projected_delta > total_budget * 0.05) status = 'trending_over';
  else if (confidence < 0.45) status = 'low_confidence';
  else if (projected_delta > -total_budget * 0.04) status = 'elevated_pace';
  else status = 'healthy_pace';

  // Headline driver: the heaviest at-risk category, even when the trip is fine.
  const overCats = cats.filter((c) => c.projected_delta > 0);
  const atRisk = cats.filter((c) => c.trend_status === 'exhaustion_risk' || c.trend_status === 'budget_exhausted');
  const headlineCat = overCats[0] ?? atRisk[0];
  const primary_driver = headlineCat
    ? `${headlineCat.display_name} is trending above plan`
    : 'Spending is tracking with your plan';
  const next_best_action = headlineCat
    ? `Look for lower-cost ${CATEGORY_NOUN[headlineCat.category_code] ?? 'options'}`
    : 'You’re on track. No action needed';

  return {
    status,
    uncounted_items,
    total_budget,
    actual_to_date,
    projected_total,
    projected_delta,
    confidence,
    confidence_label: confidenceLabel(confidence),
    primary_driver,
    next_best_action,
  };
}
